import { Controller } from "@hotwired/stimulus"
import { KTDataTable } from "@keenthemes/ktui"

export default class extends Controller {
  static values = { url: String, pageSize: { type: Number, default: 10 } }
  static targets = ["table", "rolling", "volume", "sequence"]
  static outlets = ["date-range"]

  connect() {
    this.queryParams = {}

    const urlParams = new URLSearchParams(window.location.search)
    for (const [key, value] of urlParams.entries()) {
      this.queryParams[key] = value
    }

    this.lastTargetDate = null

    this.table = new KTDataTable(this.tableTarget, {
      apiEndpoint: this.urlValue,
      requestHeaders: {
        "Content-Type": "application/json",
      },
      pageSize: this.pageSizeValue,
      pageSizes: [10, 20, 50, 100],
      stateSave: true,
      infoEmpty: "暂无数据",
      mapRequest: (params) => {
        const endpoint = this.table?._config?.apiEndpoint || this.urlValue
        if (endpoint) {
          const url = new URL(endpoint, window.location.origin)
          url.searchParams.forEach((v, k) => {
            if (!params.has(k)) params.set(k, v)
          })
        }

        Object.entries(this.queryParams).forEach(([k, v]) => params.set(k, v))

        const date = params.get("target_date")
        if (date && date !== this.lastTargetDate) {
          params.set("include_metrics", "true")
          this.lastTargetDate = date
        } else {
          params.delete("include_metrics")
        }

        return params
      },
      mapResponse: (r) => r.table,
    })

    this.table.on("drew", () => this.applyCustomCellStyles())

    this.table.on("fetched", (e) => {
      if (!e || !e.response) return

      // 甘肃交易策略
      if (e.response?.stream) Turbo.renderStreamMessage(e.response.stream)

      // 动态更新 metadata（表头日期、轮询时间窗口等）
      if (e.response?.metadata) {
        const { deal_date, poll_start_time, poll_end_time } =
          e.response.metadata

        if (deal_date) {
          const header = this.element.querySelector(
            '[data-table-header="deal-time"]',
          )
          if (header) header.textContent = `成交时间 (${deal_date})`
        }

        if (poll_start_time) {
          this.element.dataset.pollStartTimeValue = poll_start_time
          this.element.dataset.pollEndTimeValue = poll_end_time
        } else {
          delete this.element.dataset.pollStartTimeValue
          delete this.element.dataset.pollEndTimeValue
        }
      }

      if (this.hasRollingTarget && this.hasVolumeTarget) {
        const {
          rolling: { series: rollingSeries },
          volume: { series: volumeSeries },
        } = e.response.chart

        this.application
          .getControllerForElementAndIdentifier(this.rollingTarget, "chart")
          .chart?.updateSeries(rollingSeries)

        this.application
          .getControllerForElementAndIdentifier(this.volumeTarget, "chart")
          .chart?.updateSeries(volumeSeries)
      }
    })
  }

  disconnect() {
    this.table?.dispose()
  }

  reload(event) {
    const { detail, target } = event || {}

    if (detail?.params)
      this.queryParams = { ...this.queryParams, ...detail.params }
    else if (target?.name && target?.value !== undefined)
      this.queryParams[target.name] = target.value

    if (this.table) {
      this.table._config.apiEndpoint ||= this.urlValue
      this.table.goPage(1)
      this.table.reload()
    }
  }

  changeTab(event) {
    event.preventDefault()

    const url = event.currentTarget.href
    if (!url) return

    const { startDate, endDate } = event.currentTarget.dataset

    if (startDate) this.queryParams.target_date_start = startDate
    if (endDate) this.queryParams.target_date_end = endDate

    if (startDate && endDate && this.hasDateRangeOutlet)
      this.dateRangeOutlet.setRange(startDate, endDate)

    this.table._config.apiEndpoint = url
    this.table.goPage(1)
    this.table.reload()

    this.element
      .querySelectorAll(".kt-tab-toggle")
      .forEach((tab) =>
        tab.classList.toggle("active", tab === event.currentTarget),
      )
  }

  dateRangeFilter(event) {
    const { startDate, endDate } = event.detail || {}
    if (startDate) this.queryParams.target_date_start = startDate
    if (endDate) this.queryParams.target_date_end = endDate
    if (this.table) {
      this.table._config.apiEndpoint ||= this.urlValue
      this.table.goPage(1)
      this.table.reload()
    }
  }

  applyCustomCellStyles() {
    const tableElement = this.tableTarget.querySelector("table")
    if (!tableElement) return

    const dataHeaders = Array.from(
      tableElement.querySelectorAll("thead th[data-kt-datatable-column]"),
    )
    const rows = tableElement.querySelectorAll("tbody tr")

    dataHeaders.forEach((th, index) => {
      if (!th.dataset.cellClass) return

      const classes = th.dataset.cellClass.split(" ")

      rows.forEach((row) => {
        const td = row.cells[index]
        if (td) td.classList.add(...classes)
      })
    })
  }
}
